import PropTypes from 'prop-types';
import SoftTypography from "components/SoftTypography";
import Highlighted from './highlighted';
import SoftBox from 'components/SoftBox';
import SoftBadge from 'components/SoftBadge';
import SoftEditor from "components/SoftEditor";
import SoftButton from 'components/SoftButton';
import { useState, useEffect } from 'react';


function Result({transcript, isEditMode, editedText, onTextChange, handleSaveTranscript, handleCancelTranscript, searchTerm, isTranscriptEdited, setIsTranscriptEdited }) {
  
    const splitTranscriptBySpeakers = (transcriptText) => {
        const speakerRegex = /(Speaker \d+:)/g;
        return transcriptText.split(speakerRegex).slice(1); // Remove the first element if it's an empty string before first speaker label
    };

    const [transcriptParts, setTranscriptParts] = useState(() => splitTranscriptBySpeakers(transcript.data.results.channels[0].alternatives[0].paragraphs.transcript));

    useEffect(() => {
        setTranscriptParts(splitTranscriptBySpeakers(transcript.data.results.channels[0].alternatives[0].paragraphs.transcript));
        console.log('Transcript updated in Result component:', transcript);
    }, [transcript]);


    const handleEditableChange = (event, index) => {
        const updatedParts = [...transcriptParts];
        updatedParts[index] = event.target.innerText; // Update the part that has been edited
        setTranscriptParts(updatedParts);
        setIsTranscriptEdited(true);
    
        // Rebuild the full transcript to send it back to the parent component
        const fullTranscript = updatedParts.join(' '); // Assuming parts are joined by spaces
        onTextChange(fullTranscript); // Propagate changes up to the parent
    };


    const renderTranscriptWithSpeakers = () => {
        if (!transcript?.data?.results?.channels[0]?.alternatives[0]?.paragraphs?.transcript) {
            return null; // Return null or any appropriate value indicating that the transcript is not available
        }
        const transcriptText = transcript.data.results.channels[0].alternatives[0].paragraphs.transcript;
        const transcriptParts = splitTranscriptBySpeakers(transcriptText);
    
        return transcriptParts.map((part, index) => {
            const isSpeakerLabel = index % 2 === 0;
            const trimmedPart = part.trimStart();
    
            if (isSpeakerLabel) {
                // Speaker label
                return (
                    <SoftBox key={index} display="flex" alignItems="center">
                        <SoftBadge
                            variant="contained"
                            color={getBadgeColor(part)} // Dynamic color based on speaker ID
                            size="md"
                            badgeContent={part}
                            circular
                            container
                            sx={{ pl: 1, pb: 1 }}
                        />
                    </SoftBox>
                );
            } else {
                // Speaker text
                return (
                    <div 
                        key={index} 
                        contentEditable 
                        onInput={(e) => handleEditableChange(e, index)} 
                        suppressContentEditableWarning
                        //style={{ paddingTop: '1.5rem', paddingLeft: '1.7rem', paddingRight: '3rem' }}
                        style={{ border: 'none', boxShadow: 'none'}}
                    >
                        <SoftTypography pt={0.5} pl={1.7} pr={3} key={index} variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }} >
                            {highlightSearchTerm(trimmedPart, searchTerm)}
                        </SoftTypography>
                    </div>
                );
            }
        });
    };

    const getBadgeColor = (speakerLabel) => {
        const match = speakerLabel.match(/\d+/);
        if (!match) {
            console.error('No speaker ID found in label:', speakerLabel);
            return 'error'; // Default color or handling for unexpected labels
        }
        const speakerId = parseInt(match[0], 10);
        switch (speakerId) {
            case 0:
                return 'secondary'; 
            case 1:
                return 'dark';
            case 2:
                return 'warning'; 
            case 3:
                return 'info'; 
            case 4:
                return 'light'; 
            case 5:
                return 'success'; 
            default:
                return 'error'; 
        }
    };

    const highlightSearchTerm = (text, searchTerm) => {
        if (!searchTerm) return text;
    
        const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
        return parts.map((part, index) => part.toLowerCase() === searchTerm.toLowerCase()
            ? <span key={index} style={{ backgroundColor: 'lightblue' }}>{part}</span>
            : part);
    };
    
    
    
    const hasSentimentAnalysis = Array.isArray(transcript.sentiment_analysis_results) && transcript.sentiment_analysis_results.length > 0;
    const hasEntityDetection = Array.isArray(transcript.entities) && transcript.entities.length > 0;
    const hasUtterances = Array.isArray(transcript.data.results.utterances) && transcript.data.results.utterances.length > 0;

    {/*const getBadgeColor = (speaker) => {
        switch (speaker) {
            case 0:
                return 'secondary'; // Adjust color names as per your color scheme
            case 1:
                return 'dark';
            // Add more cases for other speakers if needed
            default:
                return 'info';
        }
    };*/}

    
    return (
        <div>
            <SoftTypography variant="body 2" color="button" textAlign='left' fontWeight="regular">
                {hasSentimentAnalysis && hasEntityDetection ? (
                    // Render with both sentiment analysis and entity detection
                    transcript.sentiment_analysis_results.map((result, index) => (
                        <Highlighted key={index} text={result.text} sentiment={result.sentiment} entities={transcript.entities} />
                    ))
                ) : hasSentimentAnalysis ? (
                    // Render with only sentiment analysis
                    transcript.sentiment_analysis_results.map((result, index) => (
                        <Highlighted key={index} text={result.text} sentiment={result.sentiment} entities={[]} />
                    ))
                ) : hasEntityDetection ? (
                    // Render with only entity detection
                    <Highlighted text={transcript.data.results} sentiment={null} entities={transcript.entities} />
                ) : hasUtterances ? (
                    <div>
                        <SoftBox >
                            {renderTranscriptWithSpeakers()}
                        </SoftBox>
                    </div>
                ) : (
                    <div>
                        <SoftBox >
                            {renderTranscriptWithSpeakers()}
                        </SoftBox>
                    </div>
                )}
            </SoftTypography>
        </div>
    );
}

Result.propTypes = {
    transcript: PropTypes.shape({
        data: PropTypes.shape({
            results: PropTypes.shape({
                utterances: PropTypes.arrayOf(PropTypes.shape({
                    transcript: PropTypes.string,
                    speaker: PropTypes.number, // Adjust this type based on actual data (number or string)
                })),
                channels: PropTypes.arrayOf(PropTypes.shape({
                    alternatives: PropTypes.arrayOf(PropTypes.shape({
                        paragraphs: PropTypes.shape({
                            transcript: PropTypes.string,
                        }),
                    })),
                })),
            }),
        }),
        sentiment_analysis_results: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string,
            sentiment: PropTypes.string,
        })),
        entities: PropTypes.arrayOf(PropTypes.shape({
            text: PropTypes.string.isRequired,
            entity_type: PropTypes.string.isRequired,
        })),
        // You might need to include other properties here based on your usage
    }),
    isEditMode: PropTypes.bool,
    editedText: PropTypes.string,
    onTextChange: PropTypes.func,
    handleSaveTranscript: PropTypes.func.isRequired,
    handleCancelTranscript: PropTypes.func.isRequired,
    searchTerm: PropTypes.string,
    isTranscriptEdited: PropTypes.bool,
    setIsTranscriptEdited: PropTypes.func,
};


export default Result;